import {create} from 'zustand';
import {persist, createJSONStorage} from 'zustand/middleware';

/**
 * Tracks how many new support tickets have arrived since the current admin last
 * looked at Customer Support. Feeds the sidebar badge + the beep.
 *
 * Three buckets are watched independently — "incoming" (new/unassigned AI &
 * human states), "escalated", and "awaiting" (customers waiting on a reply
 * from this agent) — each with its own high-water mark and init flag, so
 * one bucket's query failing can never block the other. Watching escalated on
 * its own is what lets an AI_ACTIVE → ESCALATED transition register even though
 * the combined total is unchanged. Persisted so a reload doesn't re-alert the
 * whole backlog.
 */
export const useTicketAlertsStore = create(
  persist(
    (set, get) => ({
      initIncoming: false,
      baseIncoming: 0,
      initEscalated: false,
      baseEscalated: 0,
      initAwaiting: false,
      baseAwaiting: 0,
      unseen: 0,
      // "Customer reply alerts" toggle for this browser: gates the awaiting
      // bucket's polling and beep. Mirrors the server's `subscribed`.
      replyAlertsOn: false,

      // Turning alerts on re-seeds the awaiting mark, so replies that were
      // already waiting don't beep the moment the toggle flips.
      setReplyAlertsOn: on =>
        set(state =>
          on && !state.replyAlertsOn
            ? {replyAlertsOn: true, initAwaiting: false}
            : {replyAlertsOn: on},
        ),

      clearUnseen: () => set({unseen: 0}),

      /**
       * Reconcile one bucket's fresh total against its high-water mark.
       * @param bucket 'incoming' | 'escalated' | 'awaiting'
       * @param total  latest queue total for that bucket
       * @param seen   true when the admin is on Customer Support (silently
       *               resync + clear the badge instead of alerting)
       * @returns number of NEW tickets detected (0 if none) so the caller beeps
       */
      observe: (bucket, total, seen) => {
        const state = get();
        const name = bucket.charAt(0).toUpperCase() + bucket.slice(1);
        const initKey = `init${name}`;
        const baseKey = `base${name}`;
        const initialized = state[initKey];
        const base = state[baseKey];

        // On the support page, or the first-ever reading for this bucket: seed
        // the mark silently (no alert). Clear the badge while actively viewing.
        if (seen || !initialized) {
          set({
            [initKey]: true,
            [baseKey]: total,
            ...(seen ? {unseen: 0} : {}),
          });
          return 0;
        }

        const delta = total > base ? total - base : 0;
        // Always follow the latest total (also lowers the mark as tickets get
        // handled, so a later rise re-alerts correctly).
        set({[baseKey]: total, unseen: state.unseen + delta});
        return delta;
      },

      reset: () =>
        set({
          initIncoming: false,
          baseIncoming: 0,
          initEscalated: false,
          baseEscalated: 0,
          initAwaiting: false,
          baseAwaiting: 0,
          unseen: 0,
          replyAlertsOn: false,
        }),
    }),
    {
      name: 'ticketAlerts',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
